/**
 * The Governance surface: the programme's own governance record, or the
 * change case's.
 *
 * The panels have their own address, so the reader meets the approval record
 * once, after the thing being approved.
 *
 * @module router/programme-editor/GovernanceRoute
 */

import React from "react";

import { useNavigate } from "react-router";

import { ChangeCaseGovernancePanel } from "@/components/governance/ChangeCaseGovernancePanel";
import { ProgrammeGovernancePanel } from "@/components/governance/ProgrammeGovernancePanel";
import { NamesUnavailableAlert } from "@/components/shared/NamesUnavailableAlert";
import { userDisplayName } from "@/components/shared/user-display-name";
import { EmptyState } from "@/components/ui";
import { pathForStateKey } from "@/router/programme-editor/surfaces";
import { useWorkspaceOutletContext } from "@/router/workspace-context";

import { useEditorBase } from "./SurfaceLayout";

export function GovernanceRoute(): React.ReactElement {
  const navigate = useNavigate();
  const {
    client,
    session,
    editorSync,
    platformUsers,
    platformDataError,
    refreshPlatformWorkspace,
  } = useWorkspaceOutletContext();
  const { record, unsaved, reloadProgramme } = editorSync;
  const base = useEditorBase();

  if (!record) {
    return (
      <EmptyState
        icon="gavel"
        title="No governance record yet"
        description="The record appears once the programme has been opened."
        data-testid="governance-empty"
      />
    );
  }

  if (record.changeCaseId) {
    return (
      <ChangeCaseGovernancePanel
        // Keyed on the change case alone. saveRevision bumps on every
        // autosave, and the workflow state is what a refused action's own
        // re-read moves, so keying on either remounts the panel out from under
        // the reason it was about to report.
        key={record.changeCaseId}
        client={client}
        session={session}
        programme={record}
        changeCaseId={record.changeCaseId}
        hasUnsavedChanges={unsaved}
        hrefFor={(target) => pathForStateKey(base, target.section, target.moduleId)}
        onChanged={async () => {
          await reloadProgramme();
          await refreshPlatformWorkspace();
        }}
        onReplacementCreated={async (replacementProgrammeId) => {
          await refreshPlatformWorkspace();
          navigate(`/programmes/${replacementProgrammeId}/overview/identity`);
        }}
        onDiscarded={async () => {
          await refreshPlatformWorkspace();
          navigate(`/programmes/${record.id}/overview/identity`);
        }}
      />
    );
  }

  return (
    <>
      {platformDataError ? (
        <NamesUnavailableAlert
          onRetry={refreshPlatformWorkspace}
          data-testid="governance-names-error"
        />
      ) : null}
      <ProgrammeGovernancePanel
        // Keyed on the programme alone. Keying on the workflow state as well
        // remounted the panel on the move its own override had just made, which
        // threw away the reference the reader had not read yet.
        key={record.id}
        client={client}
        session={session}
        programme={record}
        hasUnsavedChanges={unsaved}
        hrefFor={(target) => pathForStateKey(base, target.section, target.moduleId)}
        nameFor={(userId) =>
          userDisplayName(userId, {
            users: platformUsers,
            self: session.user,
            namesUnavailable: platformDataError !== null,
          })
        }
        onProgrammeChanged={async () => {
          await reloadProgramme();
          await refreshPlatformWorkspace();
        }}
        onRevisionCreated={async (nextProgrammeId) => {
          await refreshPlatformWorkspace();
          // The layout opens whatever the address names.
          navigate(`/programmes/${nextProgrammeId}/overview/identity`);
        }}
        onRelationshipOpen={(relatedProgrammeId) => {
          // Through the registry, not spelled here: this is the third place a
          // section address was built by hand, and the other two were both
          // pointing at a surface that had since moved.
          const to = pathForStateKey(`/programmes/${relatedProgrammeId}`, "snapshot");
          if (to) {
            navigate(to);
          }
        }}
      />
    </>
  );
}
